/**
 * FiltersView Component
 *
 * This file defines the FiltersView component, which provides a user interface
 * for applying filters through a combination of checkboxes and a date picker.
 * Users can select categories, choose a date, and reset or apply their filter choices.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { View, Text, StyleSheet, ScrollView, Platform } from "react-native";
import Checkbox from "../../../components/Checkbox/Checkbox";
import { COLORS, STRINGS } from "../../../constants";
import EventStore from "../../../storage/eventStore";
import { useNavigation } from "@react-navigation/native";
import { ActivityIndicator } from "react-native-paper";
import { useRoute } from "@react-navigation/native";
import DatePicker from "../../../components/DatePicker/DatePicker";
import { logAnalyticsEvent } from "../../../utils/firebase";

const FiltersView = () => {
  logAnalyticsEvent("screen_view", { name: "VBGo-FiltersView" });

  const categories = EventStore((state) => state.categories);
  const fetchCategories = EventStore((state) => state.fetchCategories);
  const applyFilters = EventStore((state) => state.applyFilters);
  const selectedCategories = EventStore((state) => state.selectedCategories);
  const loadingCategories = EventStore((state) => state.loadingCategories);
  const searchText = EventStore((state) => state.searchText);

  const [localCategories, setLocalCategories] = useState(selectedCategories || []);

  const navigation = useNavigation();
  const route = useRoute();

  const today = new Date();
  const oneWeekLater = new Date();
  oneWeekLater.setDate(today.getDate() + 6);

  const selectedFromDate = EventStore((state) => state.selectedFromDate);
  const selectedToDate = EventStore((state) => state.selectedToDate);

  const [fromDate, setFromDate] = useState(selectedFromDate ? new Date(selectedFromDate) : null);
  const [toDate, setToDate] = useState(selectedToDate ? new Date(selectedToDate) : null);
  const { resetVerify } = EventStore();
  /**
   * Effect: Detect "Clear All" action
   *
   * When the "Clear All" button is pressed, `clearFilters` is set to `true` in route params.
   * This effect listens for that change, triggers `handleReset()` to reset filters,
   * and then clears the `clearFilters` param to avoid repeated resets.
   */
  useEffect(() => {
    if (route.params?.clearFilters) {
      resetVerify(true);
      handleReset();
      navigation.setParams({ clearFilters: false });
    }
  }, [route.params?.clearFilters]);

  /**
   * Effect: Apply filters when navigating back
   *
   * This effect listens for the screen's "beforeRemove" event, which fires when the user presses
   * the back button or navigates away. If navigation can go back, it calls `handleFilterResults()`
   * to ensure filters are applied before leaving the screen.
   *
   * The dependency array `[localCategories, fromDate, toDate]` ensures the effect reattaches
   * whenever the filter selections change.
   */
  useEffect(() => {
    const unsubscribe = navigation.addListener("beforeRemove", () => {
      if (navigation.canGoBack()) {
        handleFilterResults(); // Apply filters when back is pressed
      }
    });

    return unsubscribe; // Cleanup listener when effect re-runs or unmounts
  }, [localCategories, fromDate, toDate]);

  /**
   * Effect: Fetches Categories List on mount
   *
   */
  useEffect(() => {
    fetchCategories();
  }, []);

  /**
   * handleCheckboxChange
   *
   * Toggles selected categories locally.
   */
  const handleCheckboxChange = useCallback((category) => {
    resetVerify(true);
    setLocalCategories((prev) => {
      if (prev.includes(category)) {
        return prev.filter((c) => c !== category);
      } else {
        return [...prev, category];
      }
    });
  }, []);

  /**
   * handleReset
   *
   * Resets local filters.
   */
  const handleReset = () => {
    setLocalCategories([]);
    setFromDate("");
    setToDate("");
  };

  /**
   * handleFilterResults
   *
   * Updates Zustand store with selected filters and applies them.
   */
  const handleFilterResults = async () => {
    EventStore.setState({
      selectedCategories: localCategories,
      selectedFromDate: fromDate || "",
      selectedToDate: toDate || "",
    });

    await applyFilters();
  };

  const renderCategories = useMemo(() => {
    return categories.map((category) => (
      <View key={category.name} style={styles.checkboxContainer}>
        <Checkbox
          label={category.name}
          value={localCategories.includes(category.name)}
          onChange={() => handleCheckboxChange(category.name)}
          color={COLORS.primary}
          uncheckedColor={COLORS.border}
        />
      </View>
    ));
  }, [localCategories, categories]);

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.scrollContainer}
      keyboardShouldPersistTaps="handled"
    >
      {/* Date Picker */}
      {/* <View style={styles.datePickerContainer}>
        <Text style={styles.datePickerLabel}>{STRINGS.datePickerLabel}</Text>
        <DatePicker
          fromDate={!fromDate || isNaN(new Date(fromDate).getTime()) ? null : new Date(fromDate)}
          toDate={!toDate || isNaN(new Date(toDate).getTime()) ? null : new Date(toDate)}
          onFromDateChange={(date) => {
            setFromDate(date);
            resetVerify(true);
          }}
          onToDateChange={(date) => {
            setToDate(date);
            resetVerify(true);
          }}
        />
      </View> */}

      {/* Category Selection */}
      <View style={styles.container}>
        <Text style={styles.heading}>{STRINGS.category}</Text>

        {loadingCategories ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator animating={true} size="small" color={COLORS.primary} />
            <Text style={styles.loadingText}>{STRINGS.loadingCategories}</Text>
          </View>
        ) : categories.length === 0 ? (
          <View style={styles.noCategoriesContainer}>
            <Text style={styles.noCategoriesText}>{STRINGS.noCategories}</Text>
          </View>
        ) : (
          renderCategories
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: COLORS.backgroundColor,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingBottom: 150,
    alignItems: "stretch",
  },
  datePickerContainer: {
    alignSelf: "flex-start",
    marginTop: 24,
    marginBottom: 12,
  },
  datePickerLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: COLORS.textDarker,
    marginBottom: 16,
    textAlign: "left",
  },
  container: {
    width: "100%",
    marginTop: 16,
  },
  heading: {
    fontSize: 16,
    marginBottom: 16,
    fontWeight: "600",
    color: COLORS.textDarker,
    textAlign: "left",
  },
  checkboxContainer: {
    marginBottom: 12,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.backgroundColor,
    paddingTop: 16,
    paddingBottom: 32,
    gap: 16,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  loadingText: {
    fontSize: 14,
    color: COLORS.textDark,
  },
  noCategoriesContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  noCategoriesText: {
    fontSize: 14,
    color: COLORS.textDark,
  },
});

export default FiltersView;
